import { useSession } from 'next-auth/react';
import { useMemo } from 'react';
import { NavItemSchema } from '@/config/navigation-schema';
import { hasRegulationProfessionalRole } from '@/lib/access-check';

export interface NavigationAccessUser {
  allowedFeatures?: string[];
  hasProfessionalAccess?: boolean;
  hasQuotaAccess?: boolean;
  hasEliteAccess?: boolean;
  isAdmin?: boolean;
  /** Raw role ids from the session (e.g. 'compliance_trial'). Used for
   * scoped sidebar variants; access itself still resolves via flags. */
  roles?: string[];
}

export interface NavigationAccessResult {
  isAccessible: boolean;
  needsLogin: boolean;
  requiresUpgrade: boolean;
  isAlphaFeature: boolean;
}

function checkTierAccess(tier: string, user: NavigationAccessUser | null | undefined): boolean {
  if (tier === 'basic') return true;
  // Quota-trial users pass the professional tier gate; the featureId check
  // in checkNavigationAccess still restricts them to their five features.
  // Every professional-tier item must therefore carry a featureId.
  // The regulation package passes the same way: its featureIds cover only
  // the regulation items, so every other professional item stays locked.
  if (tier === 'professional') {
    return (
      (user?.hasProfessionalAccess ?? false) ||
      (user?.hasQuotaAccess ?? false) ||
      hasRegulationProfessionalRole(user?.roles ?? [])
    );
  }
  if (tier === 'elite') return user?.hasEliteAccess ?? false;
  if (tier === 'admin') return user?.isAdmin ?? false;
  return false;
}

/**
 * Pure function that checks whether a user can access a navigation item.
 * Use this when computing access outside of React component render
 * (e.g. inside loops or event handlers). For component-level access
 * checks, prefer the `useNavigationAccess` hook.
 */
export function checkNavigationAccess(
  item: NavItemSchema | null,
  user: NavigationAccessUser | null | undefined,
): NavigationAccessResult {
  if (!item) {
    return { isAccessible: false, needsLogin: false, requiresUpgrade: false, isAlphaFeature: false };
  }

  // Admin users ALWAYS have full access to all features and items.
  if (user?.isAdmin) {
    return { isAccessible: true, needsLogin: false, requiresUpgrade: false, isAlphaFeature: false };
  }

  if (item.isAlpha) {
    return { isAccessible: false, needsLogin: false, requiresUpgrade: false, isAlphaFeature: true };
  }

  const hasFeatureAccess = item.featureId
    ? user?.allowedFeatures?.includes(item.featureId) ?? false
    : true;

  const hasTierAccess = checkTierAccess(item.tier, user);

  const isAccessible = hasFeatureAccess && hasTierAccess;
  const needsLogin = !user && item.tier !== 'basic';
  const requiresUpgrade = !isAccessible && !needsLogin;

  return { isAccessible, needsLogin, requiresUpgrade, isAlphaFeature: item.isAlpha ?? false };
}

/**
 * React hook that computes navigation access for a given item using
 * the current session. Delegates to `checkNavigationAccess` internally.
 */
export function useNavigationAccess(item: NavItemSchema | null): NavigationAccessResult {
  const session = useSession();

  return useMemo(
    () => checkNavigationAccess(item, session.data?.user),
    [item, session.data?.user],
  );
}
